// MACHINERY V2 (Stroje) — Route layout (M2.0 foundation).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §2.2 / §8 M2.0.
// Mirror source: settings/v2/SettingsV2Layout.tsx (which itself mirrors
// midas/components/v3/MidasV3Layout.tsx) — root div + hoisted Radix provider +
// side-effect token-sheet import.
//
// This is the coexistence entry point for the whole v2 sub-tree:
//   • Owns the section-scoped root class
//     `machinery-v2-root p-<palette> mod-stroje`. ALL v2 tokens resolve only
//     under that root, so neither palette can leak into the live app and the
//     swap is a runtime class toggle driven by `machineryV2UiSlice`.
//   • Imports the section token sheet ONCE as a side effect (kept OUT of
//     src/index.scss so retiring v2 is one folder delete away).
//   • Hoists ONE Radix TooltipProvider to the layout root so fleet capability
//     badges, kebab menus and dialogs all share it. MIDAS shipped a P6 crash
//     hotfix precisely because a provider sat too deep in the tree — do not
//     re-introduce per-subtree providers below this point.
//
// PORTAL RULE — READ BEFORE ADDING THE FIRST TOOLTIP / MENU / DIALOG (M2.1+).
// Radix Tooltip.Content, DropdownMenu.Content, Popover.Content and Dialog.Content
// render through a portal to document.body, i.e. OUTSIDE `.machinery-v2-root`,
// where --card / --border / --ink / --shadow / --mod-accent DO NOT RESOLVE. A
// portalled node styled with those vars paints unthemed. Nothing portals in M2.0
// (no tooltip is rendered yet), so this is a guardrail, not a live bug.
// Countermeasure (ported from SettingsV2Layout.tsx:19-25, itself the
// ConfirmDialogV3 / NewChatDialogV3 pattern): portals ARE permitted to
// document.body PROVIDED the portalled node's own wrapper re-carries
// `machinery-v2-root p-${palette} mod-stroje` AND sets inline
// background:"transparent" — the transparent override defeats
// .machinery-v2-root's opaque var(--bg) paint (which would otherwise fill the
// screen behind an overlay) while still letting the scoped CSS vars resolve
// inside the portalled subtree. Read `palette` from `machineryV2UiSlice` at the
// portalling component, not from a captured constant. Non-portalled children
// need none of this.
// M2.1 note: the rule is LIVE, not theoretical, from here on — ChromeNavV4's
// right cluster mounts the first two real portals (user menu + language menu).
// Both are implemented in chrome/ChromeRightMenus.tsx, whose shared
// MenuContent wrapper is the single place the countermeasure lives.
//   • Wraps <Outlet/> — and ONLY <Outlet/> — in feedback/ErrorBoundaryV2. One
//     insertion covers all six v2 routes, and keeping ChromeNavV4 outside the
//     boundary means a page that throws still leaves the user a working nav to
//     escape with. Added after a null spec field white-screened
//     /machinery-v2/catalog: React 18 unmounts the whole root on an uncaught
//     render error, and until then nothing in this sub-tree caught anything.
//   • Does NOT render the global MachineID top bar: the shared app shell
//     renders above and this layout fills the remainder
//     (flex:1; height:100%; minHeight:0; overflow:hidden).
//   • ChromeNavV4 (M2.1) mounts INSIDE this root, above the <Outlet/> — it is
//     v2 PAGE chrome today and the PERMANENT platform nav from M2.12 (§9 D2),
//     so it belongs to the layout, not to any individual v2 page.
//
// ============================================================
//  THE PHONE BRANCH (M2.16a)
// ============================================================
// BELOW 768 ONLY, ChromeNavV4's 52px bar is replaced by the 48px phone header
// (mobile/README.md §Standards: "Phone chrome: 48px header (logo · avatar ·
// hamburger). Tablet keeps the full desktop ChromeBar"), and the hamburger
// opens MobileDrawer. At 768-1279 the full bar stays — screenshot
// 07-v1-tablet-768.png draws it. The 768 boundary is read through
// `useV2MediaQuery(V2_PHONE)` — the module's single breakpoint vocabulary
// (F-A) — never through MUI's useMediaQuery and no longer through the
// integer-width shared hook.
//
// THE DRAWER IS MOUNTED HERE, deliberately, and that is what makes it PURE
// GLOBAL NAV: this layout wraps the WHOLE v2 subtree (fleet, katalog, profile,
// SR, add), so anything in the drawer appears on all five. Page actions belong
// to their page — the fleet's "Spravovat skupiny" is a kebab on the fleet
// header for exactly this reason.
//
// It sits OUTSIDE ErrorBoundaryV2 for the same reason ChromeNavV4 does: a page
// that throws still leaves the user a working nav to escape with.
//
// >>> UNMOUNTING THE DRAWER DOES NOT CLOSE IT — THE FLAG IS RESET EXPLICITLY.
// <<< `menuOpen` belongs to THIS layout, which spans every v2 route and never
// unmounts, while the drawer is `{isPhone && ...}`. A resize or rotation past
// 768 therefore removed the panel from the DOM and left the flag TRUE, with no
// mounted control able to clear it; coming back under 768 re-opened the nav
// sheet and its scrim over the fleet with no user gesture. The effect below is
// what makes "leaving phone closes the menu" true, and it is the only thing
// that does. This comment previously claimed the unmount was sufficient; it was
// not.
//
// Styling stack (ticket §3, decision D1 — locked): inline style objects for
// layout + the scoped CSS-var token sheet + Radix primitives consumed directly.
// No Tailwind, no shadcn, no MUI, no @emotion, no `sx`, no `dt` tokens.
import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import * as RadixTooltip from "@radix-ui/react-tooltip";

import { useAppSelector } from "../../../shared/hooks/sharedHooks";
import { V2_PHONE, useV2MediaQuery } from "./useV2MediaQuery";
import ChromeNavV4 from "./chrome/ChromeNavV4";
import MobileDrawer from "./chrome/MobileDrawer";
import MobilePhoneHeader from "./chrome/MobilePhoneHeader";
import ErrorBoundaryV2 from "./feedback/ErrorBoundaryV2";

// Side-effect imports — the v2 token sheet, the mobile layout sheet and the
// below-1280 touch-target sheet. All three are scoped to .machinery-v2-root and
// loaded ONCE here for the whole sub-tree. They are three files because the
// project's 300-line cap forced two splits: the token sheet stands at 298, and
// the mobile sheet crossed the cap at M2.16e, which moved every touch-floor rule
// into the third. Each explains its own seam in its own header. Order is
// tokens -> layout -> touch, so a floor is never overridden by the atom it
// floors.
import "./machinery-v2-tokens.scss";
import "./mobile/machinery-v2-mobile.scss";
import "./mobile/machinery-v2-touch.scss";

const MachineryV2Layout: React.FC = () => {
  const palette = useAppSelector((s) => s.machineryV2Ui.palette);
  // F-A: below 768 is read as the media QUERY, not as an integer width —
  // one predicate for the whole module, and the same one any paired CSS
  // fences on. See useV2MediaQuery.ts.
  const isPhone = useV2MediaQuery(V2_PHONE);
  const [menuOpen, setMenuOpen] = useState(false);

  // See the header. An effect is safe HERE, where the render-body pattern is
  // used elsewhere in this module (useFleetFilterDraft's open transition):
  // the only dependency is one boolean off `matchMedia`, so there is no data
  // in the list that a poll or a refetch could re-trigger it with.
  useEffect(() => {
    if (!isPhone) setMenuOpen(false);
  }, [isPhone]);

  return (
    <RadixTooltip.Provider delayDuration={200}>
      <div
        className={`machinery-v2-root p-${palette} mod-stroje`}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          // Background + ink come from .machinery-v2-root via the token sheet.
        }}
      >
        {isPhone ? (
          <MobilePhoneHeader active="stroje" onOpenMenu={() => setMenuOpen(true)} />
        ) : (
          <ChromeNavV4 active="stroje" />
        )}
        <ErrorBoundaryV2>
          <Outlet />
        </ErrorBoundaryV2>
        {isPhone && (
          <MobileDrawer active="stroje" open={menuOpen} onOpenChange={setMenuOpen} />
        )}
      </div>
    </RadixTooltip.Provider>
  );
};

export default MachineryV2Layout;
